import {
    getAquaEditorConfig,
    installAquaAdminClient,
    ISiteEnv,
    ISiteInfo,
    loadScript,
    registerAquaEditorConfig,
} from "@syginc/tellus-client";

import {AdminApi} from "../api/api";
import {getSiteEnvUrl} from "../models/format-site-url";
import {ImagefrontTokenProvider} from "../utils/imagefront-util";
import {createAquaAdminClient} from "./admin-client";
import {IAquaEditor} from "./aqua-editor";
import {createAquaEditor} from "./create-aqua-editor";

export interface AquaEditorLoadOption {
    siteUrl: string;
    serverUrl: string;
    adminApi: AdminApi;
    imagefrontTokenProvider: ImagefrontTokenProvider;
    user?: string;
}

const editorRegistry = new Map<string, IAquaEditor>();
const inflightRegistry = new Map<string, Promise<IAquaEditor>>();
const siteEnvCache = new Map<string, ISiteEnv>();

// resourceManager.add の重複登録エラーを抑制するためのパッチ
// NOTE: このパッチを削除すると marketing-site → template-site 遷移で
//       "beryl-marketing-site.widgetstylefix is already registered" エラーが発生しエディタが起動しない。
//       抑制の正確なメカニズムは未解明だが、prototype.add を早期にラップすることで
//       CKEditor バンドルの重複登録処理が変わると推測される。要調査。
function patchCKEditorResourceManager() {
    const ck = (window as any).CKEDITOR;
    if (!ck?.resourceManager?.prototype?.add) return;
    if ((ck.resourceManager.prototype.add as any).__patched__) return;

    const original = ck.resourceManager.prototype.add;
    ck.resourceManager.prototype.add = function (name: string, ...args: unknown[]) {
        if (this.registered && this.registered[name]) {
            // beryl-template-site の aqua-editor.js が誤った siteIdentifier ("beryl-marketing-site") を
            // 持つため、marketing-site → template-site 遷移で同一名のプラグインを二重登録しようとする。
            // CKEditor の add() は重複時に throw するため、ここで early return して抑制する。
            return this.get ? this.get(name) : this.registered[name];
        }
        return original.call(this, name, ...args);
    };
    (ck.resourceManager.prototype.add as any).__patched__ = true;
}

export async function loadAquaEditor(option: AquaEditorLoadOption): Promise<IAquaEditor> {
    patchCKEditorResourceManager();
    // await より前に同期的にチェック（レースウィンドウなし）
    if (editorRegistry.has(option.siteUrl)) {
        const cachedSiteEnv = siteEnvCache.get(option.siteUrl)!;
        const cachedConfig = getAquaEditorConfig(option.siteUrl);
        const aquaAdminClient = createAquaAdminClient(
            option.adminApi,
            cachedConfig.buildImageFilename,
            option.serverUrl,
            cachedSiteEnv.assetUrl as string | undefined,
            option.imagefrontTokenProvider,
        );
        // TODO: Stop using global aquaAdminClient
        installAquaAdminClient(aquaAdminClient);
        return editorRegistry.get(option.siteUrl)!;
    }

    if (inflightRegistry.has(option.siteUrl)) {
        // 並行ロードが進行中。完了後は editorRegistry 経由でキャッシュヒットして返る。
        // ロードが失敗した場合は await が throw し、呼び出し元にエラーが伝播する（再試行は呼び出し元に委ねる）。
        await inflightRegistry.get(option.siteUrl)!;
        return loadAquaEditor(option);
    }

    // inflightRegistry を await より前に同期的に登録してレースウィンドウを排除する
    let resolveLoad!: (editor: IAquaEditor) => void;
    let rejectLoad!: (error: unknown) => void;
    const inflightPromise = new Promise<IAquaEditor>((resolve, reject) => {
        resolveLoad = resolve;
        rejectLoad = reject;
    });
    // 並行ウェイターがいない場合の unhandled rejection を防ぐ
    inflightPromise.catch((_err: unknown) => undefined);
    inflightRegistry.set(option.siteUrl, inflightPromise);

    try {
        const response = await fetch(getSiteEnvUrl(option.siteUrl));
        const siteEnv: ISiteEnv = await response.json();
        siteEnvCache.set(option.siteUrl, siteEnv);

        await loadScript(siteEnv.editorJsUrl, false, true);
        patchCKEditorResourceManager();

        const aquaEditorConfig = getAquaEditorConfig();
        registerAquaEditorConfig(option.siteUrl, aquaEditorConfig);

        const aquaAdminClient = createAquaAdminClient(
            option.adminApi,
            aquaEditorConfig.buildImageFilename,
            option.serverUrl,
            siteEnv.assetUrl as string | undefined,
            option.imagefrontTokenProvider,
        );

        // TODO: Stop using global aquaAdminClient
        installAquaAdminClient(aquaAdminClient);

        const siteInfo: ISiteInfo = {
            siteUrl: option.siteUrl,
            env: siteEnv,
            username: option.user,
        };

        const aquaEditor = await createAquaEditor(
            siteInfo,
            aquaAdminClient,
            aquaEditorConfig,
            option.imagefrontTokenProvider,
        );
        editorRegistry.set(option.siteUrl, aquaEditor);
        resolveLoad(aquaEditor);
        return aquaEditor;
    } catch (error) {
        rejectLoad(error);
        throw error;
    } finally {
        inflightRegistry.delete(option.siteUrl);
    }
}
